/* @flow */

import React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash/fp';

import ResetButton from 'app/components/commons/ResetButton';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import RadioButton from 'app/components/commons/RadioButton';
import Checkbox from 'app/components/commons/Checkbox';
import OpenChallengeFilterContent from 'app/components/challenge/OpenChallengeFilterContent';
import PlayerSelectionSortChoice from 'app/components/challenge/PlayerSelectionSortChoice';
import PlayerSearchBar from 'app/components/challenge/PlayerSearchBar';
import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import isFilteredSelector from 'app/selectors/isFilteredSelector';
import { resetProjections } from 'app/actions/challengeActions';
import {
  resetFilters,
  setSortField,
  setPlayerTypeFilter,
  toggleAutoAdvancePositionsFilter,
  toggleProjectedScoreFilter,
  toggleTeamsFilter
} from 'app/actions/playerListActions';
import t from 'app/utils/translate';
import FiltersToggleButton from 'app/components/commons/FiltersToggleButton';
import { isChallengeMLB, isChallengeNBA } from 'app/utils/challengeUtils';

type Props = {
  games: Array<*>,
  hasTeams: boolean,
  isFiltered: boolean,
  isNBA: boolean,
  isMLB: boolean,
  onReset: () => void,
  onSort: string => void,
  sortField?: string,
  statusId: number,
  onIncludedChange: boolean => void,
  onResetProjections: () => void,
  onTeamsChange: () => void,
  onToggleAutoAdvancePositions: () => void,
  onToggleProjectedScore: () => void,
  playerTypeFilters: Array<string>,
  useAverage?: boolean,
  filteredTeams: Array<string>,
  selectedTeams?: Array<string>
};

type State = {
  content?: 'filters' | 'search' | 'sort'
};

const FilterBar = styled(AnimatedGroup)(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  color: theme.color.basis1,
  overflow: 'hidden',
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.8rem'
  }
}));

const FilterBarTop = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.8rem',
    paddingLeft: 5
  }
}));

const FilterBarControls = styled.div({
  alignItems: 'stretch',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0, // flexbox hack
  textTransform: 'uppercase'
});

const Title = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  padding: '0.375em 0.5em',
  whiteSpace: 'nowrap'
});

const StyledFiltersToggleButton = styled(FiltersToggleButton)(({ theme }) => ({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  borderRight: '1px solid #d9d9d9',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'right',
    borderRight: 'none'
  }
}));

const PlayerFilter = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 0,
  flexGrow: 0,
  flexShrink: 1,
  padding: '0.375em 0.5em',
  whiteSpace: 'nowrap'
});

const Clickable = styled.div({
  display: 'flex',
  alignItems: 'center'
});

class PlayerSelectionFilterBar extends React.PureComponent<Props, State> {
  props: Props;

  state: State = {
    content: undefined
  };

  static defaultProps = {
    selectedTeams: null,
    useAverage: false,
    sortField: undefined
  };

  componentDidMount() {
    const { isMLB, isNBA, onIncludedChange, playerTypeFilters } = this.props;

    if (!playerTypeFilters.includes('included') && !isNBA && !isMLB) {
      onIncludedChange();
    }
  }

  handleResetProjections = () => {
    const { onResetProjections } = this.props;
    onResetProjections();
  };

  toggleFilters = () => {
    this.setState(state => ({
      content: state.content !== 'filters' ? 'filters' : undefined
    }));
  };

  toggleSort = () => {
    this.setState(state => ({
      content: state.content !== 'sort' ? 'sort' : undefined
    }));
  };

  toggleAutoAdvancePositions = () => {
    const { onToggleAutoAdvancePositions } = this.props;

    onToggleAutoAdvancePositions();
  };

  toggleProjectedScore = () => {
    const { onToggleProjectedScore } = this.props;

    onToggleProjectedScore();
  };

  render() {
    const {
      filteredTeams,
      isFiltered,
      onReset,
      onSort,
      onIncludedChange,
      playerTypeFilters,
      selectedTeams,
      useAverage,
      isMLB,
      isNBA,
      sortField,
      statusId,
      games,
      hasTeams,
      onTeamsChange
    } = this.props;

    const { content } = this.state;

    return (
      <Responsive tabletLandscape desktop>
        {isLargeScreen => (
          <FilterBar>
            <FilterBarTop>
              <FilterBarControls>
                <PlayerSearchBar autoFocus />
                {!isLargeScreen && (isMLB || isNBA) && (
                  <Title>
                    <Tappable component={Clickable} onTap={onIncludedChange}>
                      <RadioButton checked={playerTypeFilters.includes('included')} />
                      {'\u00a0\u00a0'}
                      Hide Inactive
                    </Tappable>
                  </Title>
                )}
                {isLargeScreen && (
                  <React.Fragment>
                    {(isMLB || isNBA) && (
                      <PlayerFilter>
                        <Checkbox
                          checked={playerTypeFilters.includes('included')}
                          onChange={onIncludedChange}
                        >
                          Hide inactive players
                        </Checkbox>
                      </PlayerFilter>
                    )}
                    <PlayerFilter>
                      <ResetButton
                        label={t(useAverage ? 'Reset Average' : 'Reset Projections')}
                        onClick={this.handleResetProjections}
                        scheme="dark"
                      />
                    </PlayerFilter>
                  </React.Fragment>
                )}
                {isLargeScreen && (
                  <StyledFiltersToggleButton
                    isFiltered={isFiltered}
                    isOpen={content === 'filters'}
                    onClick={this.toggleFilters}
                    onClear={onReset}
                    scheme="light"
                  />
                )}
              </FilterBarControls>
            </FilterBarTop>
            {content === 'filters' && (
              <OpenChallengeFilterContent
                filteredTeams={filteredTeams}
                selectedTeams={selectedTeams}
                games={games}
                hasTeams={hasTeams}
                onReset={onReset}
                onTeamsChange={onTeamsChange}
              />
            )}
            {!isLargeScreen && content === 'sort' && (
              <PlayerSelectionSortChoice
                onSort={onSort}
                sortField={sortField}
                statusId={statusId}
              />
            )}
          </FilterBar>
        )}
      </Responsive>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    autoAdvancePositions: !!state.playerList.autoAdvancePositions,
    hasTeams: !isEmpty(state.challenge.teams),
    filteredTeams: state.playerList.filteredTeams,
    selectedTeams: state.playerList.selectedTeams,
    games: state.challenge.games,
    isFiltered: !state.playerList.searchTerm && isFilteredSelector(state),
    isMLB: isChallengeMLB(state.challenge.competitionId),
    isNBA: isChallengeNBA(state.challenge.competitionId),
    racingType: state.challenge.racingType,
    playerTypeFilters: state.playerList.playerTypeFilters,
    showProjectedScore: !!state.playerList.showProjectedScore,
    sortDirection: state.challenge.sortDirection,
    sortField: state.challenge.sortField,
    statusId: state.challenge.statusId
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onIncludedChange: () => dispatch(setPlayerTypeFilter('included')),
    onToggleAutoAdvancePositions: () => dispatch(toggleAutoAdvancePositionsFilter()),
    onToggleProjectedScore: () => dispatch(toggleProjectedScoreFilter()),
    onTeamsChange: teamId => dispatch(toggleTeamsFilter(teamId)),
    onReset: () => dispatch(resetFilters()),
    onResetProjections: () => dispatch(resetProjections()),
    onSort: sortField => dispatch(setSortField(sortField))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlayerSelectionFilterBar);
